import React, { useCallback, useEffect, useState } from 'react'
import { DataResult, process, State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientPaymentProviderTransactionDto } from 'api/Wallet/Types/ClientPaymentProviderTransactionDto'
import { Loading } from 'components/Loading'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import transactionWalletOverviewColumn from './transactionWalletOverviewColumn'

const createDataState = (
  state: State,
  clientPaymentProviderTransaction: ClientPaymentProviderTransactionDto[],
  collapse = true
) => {
  const groups = state.group
  const aggregates: { field: string; aggregate: 'count' | 'sum' | 'average' | 'min' | 'max' }[] = [
    {
      field: 'amount',
      aggregate: 'sum',
    },
    {
      field: 'currency',
      aggregate: 'min',
    },
  ]
  if (groups) {
    groups.map((group) => (group.aggregates = aggregates))
  }
  let result = process(clientPaymentProviderTransaction, state)
  if (collapse) {
    result = { ...result, data: result.data.map((dataItem) => ({ ...dataItem, expanded: false })) }
  }
  return {
    result,
    dataState: state,
  }
}

export const PaymentProviderTransactionTable = ({
  clientId,
}: {
  clientId?: string
}): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const fetchData = useCallback(async () => {
    if (!clientId) {
      return []
    }
    return walletsApiClient.getPaymentProviderTransaction(clientId)
  }, [clientId]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data = [], isLoading } = useFetchOne(fetchData)

  const [dataState, setDataState] = useState<{ result: DataResult; dataState: State }>(
    createDataState(
      getPaginationInitialState({ group: [{ field: 'currency.id' }, { field: 'type.name' }] }),
      data
    )
  )

  useEffect(() => {
    if (data) {
      setDataState(createDataState(dataState.dataState, data))
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(createDataState(event.dataState, data))
  }
  const expandChange = (event: GridExpandChangeEvent) => {
    const newData = dataState.result.data.map((item) =>
      item === event.dataItem
        ? { ...item, [event.target.props.expandField as string]: event.value }
        : item
    )
    setDataState({
      result: { ...dataState.result, data: newData },
      dataState: dataState.dataState,
    })
  }

  const pageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    if (data) {
      setDataState(createDataState({ ...dataState.dataState, take, skip }, data))
    }
  }

  const cellRender = (
    td: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (td && td.props.children && props.rowType === 'groupHeader') {
      const node = td.props.children as unknown as React.ReactElement<HTMLTableCellElement>
      const children = (
        <span>
          <span>
            {node.props.children} Total: {props.dataItem.aggregates.amount.sum.toFixed(2)}{' '}
            {props.dataItem.aggregates.currency.min.id}
          </span>
        </span>
      )
      return React.cloneElement(td, td.props, children)
    }
    return td
  }

  return (
    <Loading isLoading={isLoading}>
      <Grid
        style={{ margin: 36 }}
        filterable
        sortable
        pageable={pageableSettings}
        groupable
        data={dataState.result}
        {...dataState.dataState}
        onDataStateChange={dataStateChange}
        onPageChange={pageChange}
        onExpandChange={expandChange}
        expandField='expanded'
        cellRender={cellRender}
      >
        {transactionWalletOverviewColumn
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
    </Loading>
  )
}
